import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getUserByToken } from "./crud/person.crud";
import { setUserCapabilities, setCurrentCapabilities, setSuperAdmin, setUserDetails } from "./redux/slices/authSlice";
import { setLanguages } from "./redux/slices/dashboardSlice";
import { setAppVersion } from "./redux/slices/appSlice";
import { isEmpty } from "../helpers/Functions";
import SplashScreen from "./SplashScreen";
import { getLanguages } from "./crud/masterdata.crud";
import displayToast from "../theme/partials/DisplayToast";
import { ROLE_STUDENT, ROLE_SUPER_ADMIN } from "../models/Constants";
import { 
	initAmplitude, 
	setAmplitudeUserId, 
	setAmplitudeUserProperties, 
	trackEvent
} from "../helpers/AmplitudeHelper";

// Track if Amplitude has been initialized globally
let amplitudeInitialized = false;
let currentAmplitudeUserId = null;

const LoginInit = ({
	isSuperAdmin,
	setUserCapabilities,
	setCurrentCapabilities,
	setSuperAdmin,
	currentCapabilities,
	setLanguages,
}) => {
	const [redirect, setRedirect] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		// Initialize Amplitude once globally
		if (!amplitudeInitialized) {
			initAmplitude();
			amplitudeInitialized = true;
		}

		initializeUserData(
			setUserCapabilities,
			setCurrentCapabilities,
			setSuperAdmin,
			currentCapabilities,
			setLanguages,
			setRedirect,
			true,
			setUserDetails
		);
	}, []);

	useEffect(() => {
		if (!redirect) {
			return;
		}

		if (parseInt(isSuperAdmin) === 1) {
			navigate("/admin-panel");
		} else {
			navigate("/");
		}
	}, [redirect]);

	return <SplashScreen />;
};

export async function initializeUserData(
	setUserCapabilities,
	setCurrentCapabilities,
	setSuperAdmin,
	currentCapabilities,
	setLanguages,
	callback = undefined,
	isLogin = false,
	setUserDetails
) {
	try {
		// Fetch user
		console.log("Initializing user data...");
		const userRes = await getUserByToken();
		const user = userRes?.data?.data?.[0];

		if (!user) {
			displayToast("error", "User token invalid or user not found.");
			return;
		}

		const superadmin = !isEmpty(user.is_super_admin) ? user.is_super_admin : 0;
		const currentCapabilitiesCompanyId = currentCapabilities?.company?.company_id || currentCapabilities?.company?.party_id;
		const currentCapabilitiesRoleId = currentCapabilities?.role;
		const userCompanies = user.companies || [];
		
		const currentAdditionalInfo = {
			...currentCapabilities.additional_info,
			...JSON.parse(user.additional_info),
		};
		
		let currentCompany;
		let currentRole;

		// ensuring currentCompany is updated
		if (isEmpty(currentCapabilities)) {
			currentCompany = userCompanies?.[0];
			currentRole = currentCompany?.roles?.[0];
		}
		else {
			currentCompany = userCompanies.find((comp) => comp.company_id === currentCapabilitiesCompanyId);

			if (currentCapabilitiesRoleId === ROLE_SUPER_ADMIN) {
				currentRole = {
					role_id: ROLE_SUPER_ADMIN,
					role_standard_id: ROLE_SUPER_ADMIN,
					role_name: "Otoma Admin",
				};
			}
			else {
				currentRole = currentCompany?.roles?.find((role) => role?.role_id === currentCapabilitiesRoleId);
			}
		}

		if (currentCapabilitiesRoleId === ROLE_STUDENT) {
			currentRole.role_standard_id = ROLE_STUDENT;
		}

		setSuperAdmin(superadmin);
		setUserCapabilities(user.companies);

		let capabilities = {
			company: currentCompany,
			role: currentRole?.role_id,
			role_standard_id: currentRole?.role_standard_id,
			role_name: currentRole?.role_name,
			additional_info: currentAdditionalInfo,
		};

		setCurrentCapabilities(capabilities);

		const parsedUserDetail = {
			...user,
			user_img: user?.image_file,
			additional_info: JSON.parse(user?.additional_info),
		};
		
		setUserDetails(parsedUserDetail);

		// ========== AMPLITUDE TRACKING ==========
		// Only set Amplitude user data if user has changed or is new
		const amplitudeUserId = user.email;

		if (currentAmplitudeUserId !== amplitudeUserId && !isEmpty(amplitudeUserId)) {
			currentAmplitudeUserId = amplitudeUserId;
			
			setAmplitudeUserId(amplitudeUserId);
			setAmplitudeUserProperties({
				user_id: user.party_id,
				email: user.email,
				firstName: user.firstname,
				lastName: user.lastname,
				companyId: currentCompany?.company_id,
				companyName: currentCompany?.company_name,
				roleId: currentRole?.role_id,
				roleName: currentRole?.role_name,
				isSuperAdmin: superadmin,
				userCreatedAt: user.created_at,
			});
		}

		// Fetch languages
		const langRes = await getLanguages();

		if (langRes?.data?.init?.[0]?.status === "error") {
			displayToast("error", "Data initialization error.");
		} else {
			const langs =
				langRes.data?.data?.map((lang) => ({
					key: lang.language_key,
					name: lang.language_name,
				})) || [];

			setLanguages(langs);
		}

		if (typeof callback === "function") {
			callback(true);
		}
	} catch (error) {
		console.error("Error initializing user:", error);
		displayToast("error", "Unexpected error during initialization.");
	}
}

const mapStateToProps = (state) => {
	let data = {
		appVersion: state.app.app_version,
		currentCapabilities: state.auth.current_capabilities,
		isSuperAdmin: state.auth.is_super_admin,
	};

	return data;
};

const mapDispatchToProps = {
	setUserCapabilities,
	setCurrentCapabilities,
	setSuperAdmin,
	setLanguages,
	setAppVersion,
	setUserDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginInit);
